import type { Event } from 'types';
import { _t_back } from './translateEngine';

export type EventFilterId = 'exhibitions' | 'forums' | 'lessons' | 'tours' | 'missions';

const stripHtml = (value: string): string => (
  value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
);

const toDateOnly = (value?: string | null): string => (
  String(value || '').slice(0, 10)
);

const getTodayDateOnly = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const getEventsFromDate = (): string => `${getTodayDateOnly().slice(0, 8)}01`;

export const getEventDescription = (item?: Event | null): string => {
  if (!item) {
    return '';
  }
  return stripHtml(String(_t_back(item, 'description') || item.description || ''));
};

export const hasEventDetails = (item?: Event | null): boolean => (
  getEventDescription(item).length > 0
);

export const isEventFromCalendarPeriod = (item?: Event | null): boolean => {
  if (!item) {
    return false;
  }
  const start = String(item.date_of_event_start || '');
  return Boolean(start) && start >= getEventsFromDate();
};

export const isEventRegistrationOpen = (item?: Event | null): boolean => {
  if (!item) {
    return false;
  }
  const end = toDateOnly(item.date_of_event_end || item.date_of_event_start);
  return Boolean(end) && end >= getTodayDateOnly();
};

export const sortEventsByDate = (items: Event[]): Event[] => (
  [...items].sort((a, b) => (
    String(a.date_of_event_start || '').localeCompare(String(b.date_of_event_start || ''))
  ))
);

const haystack = (item: Event): string => {
  const parts = [
    _t_back(item, 'title'),
    item.title,
    _t_back(item.category_of_event, 'title'),
    item.category_of_event?.title,
    _t_back(item.form_of_holding, 'title'),
    item.form_of_holding?.title,
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
};

const FILTER_MATCH: Record<EventFilterId, (text: string) => boolean> = {
  exhibitions: (text) => text.includes('выставк'),
  forums: (text) => text.includes('форум'),
  lessons: (text) => (
    text.includes('урок') ||
    text.includes('лекц') ||
    text.includes('обучен') ||
    text.includes('вебинар')
  ),
  tours: (text) => text.includes('экскурси'),
  missions: (text) => text.includes('бизнес-мисс'),
};

export const matchEventFilters = (
  item: Event,
  activeFilters: EventFilterId[],
): boolean => {
  if (!activeFilters.length) {
    return true;
  }
  const text = haystack(item);
  return activeFilters.some((id) => FILTER_MATCH[id](text));
};

export const getEventTagFromText = (value: string): string => {
  const text = value.toLowerCase();

  if (text.includes('форум')) {
    return 'форум';
  }
  if (text.includes('экскурси')) {
    return 'экскурсия';
  }
  if (text.includes('вебинар')) {
    return 'онлайн';
  }
  return 'оффлайн';
};

export const getEventCardTag = (item: Event): string => (
  getEventTagFromText([
    _t_back(item, 'title'),
    item.title,
    _t_back(item.category_of_event, 'title'),
    item.category_of_event?.title,
  ].filter(Boolean).join(' '))
);
